import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { ArrowLeftIcon, TrashIcon } from '@heroicons/react/24/outline';

export default function EditMemberPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [purposeTypes, setPurposeTypes] = useState([]);
  const [showAddPurposeModal, setShowAddPurposeModal] = useState(false);
  const [newPurposeLabel, setNewPurposeLabel] = useState('');
  const [workoutSlots, setWorkoutSlots] = useState([]);
  const [showAddSlotModal, setShowAddSlotModal] = useState(false);
  const [newSlotForm, setNewSlotForm] = useState({ name: '', start_time: '', end_time: '' });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    gender: '',
    date_of_birth: '',
    phone: '',
    email: '',
    password: '', // For password updates
    address: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    medical_notes: '',
    membership_plan_name: '',
    membership_start_date: '',
    membership_end_date: '',
    status: 'Active',
    workout_duration_minutes: '120',  // Default 2 hours
    height: '',
    weight: '',
    purpose_type_id: '',
    workout_slot_id: ''
  });

  useEffect(() => {
    fetchMember();
    fetchPurposeTypes();
    fetchWorkoutSlots();
  }, [id]);

  const fetchPurposeTypes = async () => {
    try {
      const res = await api.get('/api/members/purpose-types');
      setPurposeTypes(res.data.purpose_types || []);
    } catch (err) {
      console.error('Failed to fetch purpose types:', err);
    }
  };

  const fetchWorkoutSlots = async () => {
    try {
      const res = await api.get('/api/members/workout-slots');
      setWorkoutSlots(res.data.workout_slots || []);
    } catch (err) {
      console.error('Failed to fetch workout slots:', err);
    }
  };

  const handleAddSlot = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/api/members/workout-slots', newSlotForm);
      const updatedList = res.data.workout_slots || [];
      const newObj = res.data.workout_slot;
      setWorkoutSlots(updatedList);
      if (newObj) {
        setFormData(prev => ({ ...prev, workout_slot_id: newObj.id }));
      }
      setNewSlotForm({ name: '', start_time: '', end_time: '' });
      setShowAddSlotModal(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add workout slot');
    }
  };

  const handleDeleteSlot = async (slotId, slotName) => {
    if (!window.confirm(`Are you sure you want to delete the slot "${slotName}"?`)) return;
    try {
      const res = await api.delete(`/api/members/workout-slots/${slotId}`);
      setWorkoutSlots(res.data.workout_slots || []);
      if (formData.workout_slot_id === slotId || formData.workout_slot_id === String(slotId)) {
        setFormData(prev => ({ ...prev, workout_slot_id: '' }));
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete workout slot');
    }
  };

  const handleAddPurpose = async (e) => {
    e.preventDefault();
    if (!newPurposeLabel.trim()) return;
    try {
      const res = await api.post('/api/members/purpose-types', { label: newPurposeLabel.trim() });
      const updatedList = res.data.purpose_types || [];
      const newObj = res.data.purpose_type;
      setPurposeTypes(updatedList);
      if (newObj) {
        setFormData(prev => ({ ...prev, purpose_type_id: newObj.id }));
      }
      setNewPurposeLabel('');
      setShowAddPurposeModal(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add purpose type');
    }
  };

  const fetchMember = async () => {
    try {
      const response = await api.get(`/api/members/${id}`);
      const member = response.data.member;
      
      // Format dates for input fields
      setFormData({
        ...member,
        height: member.height !== null && member.height !== undefined ? member.height : '',
        weight: member.weight !== null && member.weight !== undefined ? member.weight : '',
        purpose_type_id: member.purpose_type_id || '',
        workout_slot_id: member.workout_slot_id || '',
        date_of_birth: member.date_of_birth ? member.date_of_birth.split('T')[0] : '',
        membership_start_date: member.membership_start_date ? member.membership_start_date.split('T')[0] : '',
        membership_end_date: member.membership_end_date ? member.membership_end_date.split('T')[0] : '',
        workout_duration_minutes: member.workout_duration_minutes || '120'
      });
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to fetch member details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation - only required fields
    if (!formData.first_name || !formData.phone) {
      setError('First name and phone are required.');
      return;
    }

    // Email validation (only if email is provided)
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    // Password validation (only if password is provided)
    if (formData.password && formData.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Only send password if it's been entered
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }
      
      const response = await api.put(`/api/members/${id}`, updateData);
      
      // Show success and navigate
      navigate(`/members/${id}`);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to update member. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-600">Loading member details...</div>
        </div>
      </div>
    );
  }

  if (error && !formData.first_name) {
    return (
      <div className="space-y-8">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-4 rounded-xl">
          {error}
        </div>
        <button
          onClick={() => navigate('/members')}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
        >
          <ArrowLeftIcon className="w-4 h-4" /> Back to Members
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Edit Member</h1>
          <p className="text-sm text-gray-600">Update member information</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate(`/members/${id}`)}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-4 rounded-xl mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  name="first_name"
                  required
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  Gender
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  Height (cm)
                </label>
                <input
                  type="number"
                  step="0.1"
                  name="height"
                  placeholder="e.g. 175"
                  value={formData.height}
                  onChange={handleInputChange}
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  Weight (kg)
                </label>
                <input
                  type="number"
                  step="0.1"
                  name="weight"
                  placeholder="e.g. 70"
                  value={formData.weight}
                  onChange={handleInputChange}
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  Purpose / Fitness Goal
                </label>
                <div className="flex gap-2">
                  <select
                    name="purpose_type_id"
                    value={formData.purpose_type_id}
                    onChange={handleInputChange}
                    className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
                  >
                    <option value="">Select Purpose</option>
                    {purposeTypes.map((pt) => (
                      <option key={pt.id} value={pt.id}>
                        {pt.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowAddPurposeModal(true)}
                    className="px-4 py-3 bg-orange-100 hover:bg-orange-200 text-orange-700 font-bold rounded-xl text-sm transition-colors flex items-center justify-center shrink-0"
                    title="Add Custom Purpose"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                Address
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
                className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200 resize-none"
              />
            </div>
          </div>

          {/* Account Password Update */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Password</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Password Update:</strong> Leave blank to keep current password. Enter new password to update member's login credentials.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  New Password (Optional)
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter new password (min 6 characters)"
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-500 focus:outline-none transition-all duration-200"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave blank to keep current password unchanged
                </p>
              </div>
              <div className="flex items-end">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 w-full">
                  <p className="text-xs text-gray-600 mb-2">
                    <strong>Current Login Email:</strong>
                  </p>
                  <p className="text-sm font-mono text-gray-800">
                    {formData.email || 'No email set'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Emergency Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  Contact Name
                </label>
                <input
                  type="text"
                  name="emergency_contact_name"
                  value={formData.emergency_contact_name}
                  onChange={handleInputChange}
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  name="emergency_contact_phone"
                  value={formData.emergency_contact_phone}
                  onChange={handleInputChange}
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
                />
              </div>
            </div>
          </div>

          {/* Membership Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Membership Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  Membership Plan
                </label>
                <input
                  type="text"
                  name="membership_plan_name"
                  value={formData.membership_plan_name}
                  onChange={handleInputChange}
                  placeholder="e.g., Premium Monthly, Basic Annual"
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-500 focus:outline-none transition-all duration-200"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tip: Create plans in <Link to="/membership-plans" className="text-orange-600 hover:underline">Membership Plans</Link> section
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Expired">Expired</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  name="membership_start_date"
                  required
                  value={formData.membership_start_date}
                  onChange={handleInputChange}
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  End Date *
                </label>
                <input
                  type="date"
                  name="membership_end_date"
                  required
                  value={formData.membership_end_date}
                  onChange={handleInputChange}
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  Workout Duration Per Visit *
                </label>
                <select
                  name="workout_duration_minutes"
                  required
                  value={formData.workout_duration_minutes}
                  onChange={handleInputChange}
                  className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
                >
                  <option value="30">30 Minutes</option>
                  <option value="60">1 Hour</option>
                  <option value="90">1 Hour 30 Minutes</option>
                  <option value="120">2 Hours</option>
                  <option value="150">2 Hours 30 Minutes</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Maximum allowed time per gym visit
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  Workout Timing / Slot
                </label>
                <div className="flex gap-2">
                  <select
                    name="workout_slot_id"
                    value={formData.workout_slot_id}
                    onChange={handleInputChange}
                    className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
                  >
                    <option value="">Select Timing / Slot</option>
                    {workoutSlots.map((ws) => (
                      <option key={ws.id} value={ws.id}>
                        {ws.name} {ws.start_time && ws.end_time ? `(${ws.start_time} - ${ws.end_time})` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowAddSlotModal(true)}
                    className="px-4 py-3 bg-orange-100 hover:bg-orange-200 text-orange-700 font-bold rounded-xl text-sm transition-colors flex items-center justify-center shrink-0"
                    title="Add Custom Slot"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                Medical Notes
              </label>
              <textarea
                name="medical_notes"
                value={formData.medical_notes}
                onChange={handleInputChange}
                rows="3"
                placeholder="Any medical conditions, allergies, or special requirements..."
                className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-500 focus:outline-none transition-all duration-200 resize-none"
              />
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving Changes...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/members/${id}`)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-xl transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* Modal: Add Custom Purpose */}
      {showAddPurposeModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 space-y-4 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">Add New Purpose / Goal</h3>
            <form onSubmit={handleAddPurpose} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                  Purpose Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Endurance, Rehabilitation, Marathon Prep"
                  value={newPurposeLabel}
                  onChange={(e) => setNewPurposeLabel(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddPurposeModal(false)}
                  className="px-4 py-2 text-sm border rounded-xl text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-orange-600 text-white rounded-xl hover:bg-orange-700 font-bold"
                >
                  Save Purpose
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Add & Manage Workout Slots */}
      {showAddSlotModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-5 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">Manage & Add Workout Slots</h3>
            
            {/* Existing Slots List with Delete Icon */}
            {workoutSlots.length > 0 && (
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                  Existing Slots ({workoutSlots.length})
                </label>
                <div className="space-y-1.5">
                  {workoutSlots.map((ws) => (
                    <div
                      key={ws.id}
                      className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm"
                    >
                      <span className="font-medium text-gray-800">
                        {ws.name} {ws.start_time && ws.end_time ? `(${ws.start_time} - ${ws.end_time})` : ''}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDeleteSlot(ws.id, ws.name)}
                        className="p-1 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                        title="Delete slot"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Form to Add New Slot */}
            <form onSubmit={handleAddSlot} className="space-y-4 pt-2 border-t border-gray-100">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700">
                Add New Preset Slot
              </label>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Slot Preset Name / Label *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. 6 AM - 8 AM, Morning Batch, Evening Slot"
                  value={newSlotForm.name}
                  onChange={(e) => setNewSlotForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-orange-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={newSlotForm.start_time}
                    onChange={(e) => setNewSlotForm(prev => ({ ...prev, start_time: e.target.value }))}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={newSlotForm.end_time}
                    onChange={(e) => setNewSlotForm(prev => ({ ...prev, end_time: e.target.value }))}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddSlotModal(false)}
                  className="px-4 py-2 text-sm border rounded-xl text-gray-700 hover:bg-gray-50"
                >
                  Done / Close
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-orange-600 text-white rounded-xl hover:bg-orange-700 font-bold"
                >
                  Save Slot
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}